import {
    useRef,
    useState,
    cloneElement,
    useMemo,
    isValidElement,
    useCallback,
    ReactNode,
} from 'react';
import { useElementFlyoutPosition } from '~/lib/useElementFlyoutPosition';
import { Tooltip } from 'antd';

export interface SmlTooltipProps {
    /**
     * Text shown as a tooltip. Only for simple text. No formatting. No HTML.
     */
    text: ReactNode;
    children: ReactNode;

    /**
     * Set to false to render the child element, but disable the tooltip.
     * Can be useful to make the tooltip conditionally available.
     * Set to `on-overflow` to only show the tooltip, if the child is overflowing its container.
     */
    enabled?: boolean | 'on-overflow';
    className?: string;
}

/**
 * SmartLearn Tooltip component.
 * Currently just a simplified wrapper for antd tooltip with automatic placement.
 *
 * We use `SmlTooltipInner` component to bypass all the unnecessary hooks
 * when the tooltip is disabled.
 *
 */
export function SmlTooltip({
    text,
    children,
    enabled = true,
    className,
}: SmlTooltipProps) {
    if (enabled) {
        return (
            <SmlTooltipInner
                text={text}
                children={children}
                enabled={enabled}
                className={className}
            />
        );
    }

    return <>{children}</>;
}

function SmlTooltipInner({
    text,
    children,
    enabled = true,
    className,
}: SmlTooltipProps) {
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = useRef<HTMLElement>(null);

    const { position } = useElementFlyoutPosition(elementRef, isVisible);

    const childrenWithAttr = useMemo(() => {
        if (isValidElement(children)) {
            return cloneElement(children, {
                ...children.props,
                ref: elementRef,
            });
        }

        return children;
    }, [children]);

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (open === true && enabled === 'on-overflow') {
                setIsVisible(
                    elementRef?.current?.scrollWidth >
                        elementRef?.current?.clientWidth
                );
            } else {
                setIsVisible(open);
            }
        },
        [enabled]
    );

    if (enabled === false) {
        return <>{childrenWithAttr}</>;
    }

    return (
        <Tooltip
            placement={position}
            title={text}
            onOpenChange={onOpenChange}
            open={isVisible}
            className={className}
            trigger={['hover', 'focus']}
        >
            {childrenWithAttr}
        </Tooltip>
    );
}
